import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
} from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Badge } from "ui/badge";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  BedDouble,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getRoomTypes,
  createRoomType,
  updateRoomType,
  deleteRoomType,
} from "services/roomTypeService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface RoomType {
  id: string;
  code: string;
  roomType: string;
  description: string;
  status: "active" | "inactive";
  createdOn: string;
}

export const RoomTypeMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoomType | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<RoomType | null>(null);

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    roomType: "",
    code: "",
    description: "",
    status: "active" as "active" | "inactive",
  });

  const [editFormData, setEditFormData] = useState({
    roomType: "",
    code: "",
    description: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    code: "",
    roomType: "",
  });

  const [editErrors, setEditErrors] = useState({
    code: "",
    roomType: "",
  });

  const fetchRoomTypes = async () => {
    try {
      setLoading(true);
      const res = await getRoomTypes({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      const mappedData: RoomType[] = res.items.map((item) => ({
        id: item.publicId,
        code: item.code,
        roomType: item.roomName,
        description: item.description,
        status: item.isActive ? "active" : "inactive",
        createdOn: new Date().toISOString(),
      }));

      setRoomTypes(mappedData);
      setTotalCount(res.totalCount);
    } catch (error) {
      toast.error("Failed to load room types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchRoomTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Apply sorting
  const sortedData = [...roomTypes].sort((a, b) => {
    if (!sortColumn) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any = a[sortColumn as keyof RoomType];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any = b[sortColumn as keyof RoomType];

    if (typeof aValue === "string") aValue = aValue.toLowerCase();
    if (typeof bValue === "string") bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;

    return 0;
  });

  const ROOM_CODE_REGEX = /^[A-Z0-9]{1,10}$/;
  const ROOM_TYPE_REGEX = /^[A-Za-z0-9 ]{1,20}$/;
  const ROOM_DESCRIPTION_REGEX = /^[A-Za-z0-9 ]*$/;

  const handleAdd = async () => {
    const newErrors = {
      code: "",
      roomType: "",
    };

    if (!formData.code.trim()) {
      newErrors.code = "Room Code is required";
    } else if (!ROOM_CODE_REGEX.test(formData.code)) {
      newErrors.code =
        "Room Code can contain only letters and numbers (maximum 10 characters).";
    }

    if (!formData.roomType.trim()) {
      newErrors.roomType = "Room Type is required";
    } else if (!ROOM_TYPE_REGEX.test(formData.roomType.trim())) {
      newErrors.roomType =
        "Room Type can contain only letters, numbers and spaces (maximum 20 characters).";
    }

    if (
      formData.description &&
      !ROOM_DESCRIPTION_REGEX.test(formData.description)
    ) {
      toast.error("Description can contain only letters, numbers and spaces.");
      return;
    }

    const codeExists = roomTypes.some(
      (item) => item.code.toLowerCase() === formData.code.toLowerCase(),
    );

    if (codeExists) {
      newErrors.code = "Room Code already used for another room.";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.roomType) return;

    try {
      await createRoomType({
        code: formData.code,
        roomName: formData.roomType,
        description: formData.description,
        isActive: formData.status === "active",
        createdBy: "1",
      });

      toast.success("Room type created successfully");

      setIsAddDialogOpen(false);
      resetForm();

      fetchRoomTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to create room type";

      toast.error(apiMessage);
    }
  };

  const handleEdit = (item: RoomType) => {
    setEditingItem(item);
    setEditFormData({
      code: item.code,
      roomType: item.roomType,
      description: item.description,
      status: item.status,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    const newErrors = {
      code: "",
      roomType: "",
    };

    if (!editFormData.code.trim()) {
      newErrors.code = "Room Code is required";
    } else if (!ROOM_CODE_REGEX.test(editFormData.code)) {
      newErrors.code =
        "Room Code can contain only letters and numbers (maximum 10 characters).";
    }

    if (!editFormData.roomType.trim()) {
      newErrors.roomType = "Room Type is required";
    } else if (!ROOM_TYPE_REGEX.test(editFormData.roomType.trim())) {
      newErrors.roomType =
        "Room Type can contain only letters, numbers and spaces (maximum 20 characters).";
    }

    if (
      editFormData.description &&
      !ROOM_DESCRIPTION_REGEX.test(editFormData.description)
    ) {
      toast.error("Description can contain only letters, numbers and spaces.");
      return;
    }

    const codeExists = roomTypes.some(
      (item) =>
        item.code.toLowerCase() === editFormData.code.toLowerCase() &&
        item.id !== editingItem.id,
    );

    if (codeExists) {
      newErrors.code = "Room Code already used for another room.";
    }

    setEditErrors(newErrors);

    if (newErrors.code || newErrors.roomType) return;

    try {
      await updateRoomType({
        publicId: editingItem.id,
        code: editFormData.code,
        roomName: editFormData.roomType,
        description: editFormData.description,
        isActive: editFormData.status === "active",
        updatedBy: "1",
      });

      toast.success("Room type updated successfully");

      setIsEditDialogOpen(false);
      setEditingItem(null);

      fetchRoomTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to update room type";

      toast.error(apiMessage);
    }
  };

  const handleDelete = (item: RoomType) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      await deleteRoomType(deletingItem.id, "1");

      toast.success("Room type deleted successfully");

      setIsDeleteDialogOpen(false);
      setDeletingItem(null);

      fetchRoomTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to delete room type";

      toast.error(apiMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      roomType: "",
      code: "",
      description: "",
      status: "active",
    });

    setErrors({
      code: "",
      roomType: "",
    });
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search room types or descriptions..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Room Type
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ marginTop: "10px", gap: "0px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center gap-1">
                        Code
                        {sortColumn === "code" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("roomType")}
                    >
                      <div className="flex items-center gap-1">
                        Room Type
                        {sortColumn === "roomType" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Description
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No room types found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <Badge
                            variant="outline"
                            className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs"
                          >
                            {item.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <BedDouble className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {item.roomType}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <span className="text-sm text-gray-600">
                            {item.description}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(item)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(item)}
                                className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BedDouble className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No room types found</p>
                </div>
              ) : (
                sortedData.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent style={{ padding: "14px" }}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                            <BedDouble className="w-5 h-5 text-[#5B9BD5]" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.roomType}
                            </p>
                            <Badge
                              variant="outline"
                              className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs mt-1"
                            >
                              {item.code}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
                        {item.description}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(item)}
                              className="h-8 text-xs"
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(item)}
                              className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="room types"
          />
        </Card>

        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add Room Type
              </DialogTitle>
              <DialogDescription>
                Create a new room type entry
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Room Code"
                    required
                    id="code"
                    value={formData.code}
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 10);

                      setFormData({
                        ...formData,
                        code: value,
                      });

                      setErrors({
                        ...errors,
                        code: "",
                      });
                    }}
                    placeholder="e.g., DR, ACR"
                    className={`text-sm ${
                      errors.code ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.code && (
                    <p className="text-red-500 text-xs">{errors.code}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Room Type"
                    required
                    id="roomType"
                    value={formData.roomType}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z0-9 ]/g, "")
                        .slice(0, 20);

                      setFormData({
                        ...formData,
                        roomType: value,
                      });

                      setErrors({
                        ...errors,
                        roomType: "",
                      });
                    }}
                    placeholder="e.g., Deluxe Room"
                    className={`text-sm ${
                      errors.roomType ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />

                  {errors.roomType && (
                    <p className="text-red-500 text-xs">{errors.roomType}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Description"
                  id="description"
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z0-9 ]/g, "");

                    setFormData({
                      ...formData,
                      description: value,
                    });
                  }}
                  placeholder="Brief description of the room type"
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Room Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Room Type
              </DialogTitle>
              <DialogDescription>
                Update room type information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Room Code"
                    required
                    id="edit-code"
                    value={editFormData.code}
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 10);

                      setEditFormData({
                        ...editFormData,
                        code: value,
                      });

                      setEditErrors({
                        ...editErrors,
                        code: "",
                      });
                    }}
                    placeholder="e.g., DR, ACR"
                    className={`text-sm ${
                      editErrors.code ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />
                  {editErrors.code && (
                    <p className="text-red-500 text-xs">{editErrors.code}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Room Type"
                    required
                    id="edit-roomType"
                    value={editFormData.roomType}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z0-9 ]/g, "")
                        .slice(0, 20);

                      setEditFormData({
                        ...editFormData,
                        roomType: value,
                      });

                      setEditErrors({
                        ...editErrors,
                        roomType: "",
                      });
                    }}
                    placeholder="e.g., Deluxe Room"
                    className={`text-sm ${
                      editErrors.roomType
                        ? "border-red-500 focus:ring-red-500"
                        : ""
                    }`}
                  />
                  {editErrors.roomType && (
                    <p className="text-red-500 text-xs">
                      {editErrors.roomType}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Description"
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z0-9 ]/g, "");

                    setEditFormData({
                      ...editFormData,
                      description: value,
                    });
                  }}
                  placeholder="Brief description of the room type"
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={editFormData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setEditFormData({ ...editFormData, status: value })
                    }
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingItem(null);
                  setEditErrors({ code: "", roomType: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Update Room Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Room Type
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.roomType}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
